/**
 * api.js — Central auth-aware HTTP helpers
 *
 * 1. Registers a global axios interceptor that attaches the JWT token to
 *    every outgoing axios request automatically.
 * 2. Exports `authFetch` as a drop-in replacement for fetch() that does
 *    the same for native fetch calls.
 * 3. Exports `authUrl` to append ?token= to URLs opened via window.open / <a href>.
 */
import axios from 'axios';

const TOKEN_KEY = 'depguard_token';

// ── Axios interceptor ─────────────────────────────────────────────────────────
// Runs once when this module is first imported (App.js imports it).
axios.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── authFetch ─────────────────────────────────────────────────────────────────
// Drop-in replacement for fetch() — automatically adds the Authorization header.
export function authFetch(url, options = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
  return fetch(url, { ...options, headers });
}

// ── authUrl ───────────────────────────────────────────────────────────────────
// Appends ?token=<jwt> to a URL for use with window.open() or <a href>.
// Required because browsers cannot set custom headers for navigation/download URLs.
export function authUrl(url) {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}
